<div class="container-fluid complyon-page procurement-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell procurement-shell mx-auto">
    <section class="mb-3">
      <h1 class="complyon-title procurement-title mb-2 text-l">Procurement</h1>
      <p class="complyon-subtitle procurement-subtitle mb-0 text-s">
        Requisitions, RFQs, thresholds, supplier selection, deviation approvals and POs.
      </p>
    </section>

    <section class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
      <div class="d-flex flex-wrap align-items-center gap-1">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="btn doa-tab-btn fw-semibold"
          :class="{ active: active_tab === tab.id }"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
          <span v-if="tab.count !== undefined" class="doa-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <button type="button" class="btn doa-reset-btn d-inline-flex align-items-center gap-1">
        <i class="bi bi-arrow-clockwise"></i>
        <span>Reset sample</span>
      </button>
    </section>

    <div v-if="active_tab === 'overview'">
      <section class="mb-3">
        <div class="rounded-4 border px-3 py-3" style="background:#e8f8f5;border-color:#bce9df;">
          <div class="d-flex align-items-start gap-2 text-s">
            <i class="bi bi-shield-check mt-1 text-success"></i>
            <div>
              <strong>SCM audit readiness.</strong>
              Every requisition is checked against threshold rules and supplier eligibility, with a complete evidence pack
              built to reduce SCM-related audit findings.
            </div>
          </div>
        </div>
      </section>

      <section class="row g-3 mb-3">
        <div
          v-for="card in overview_cards"
          :key="card.label"
          class="col-12 col-md-6 col-xl-3"
        >
          <article class="complyon-card procurement-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label procurement-section-title text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value procurement-section-title mt-1 text-l" :class="card.accent === 'red' ? 'text-danger' : 'text-warning-emphasis'">
              {{ card.value }}
            </div>
            <div class="complyon-stat-note procurement-section-subtitle mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="row g-3">
        <div class="col-12 col-xl-7">
          <article class="complyon-panel procurement-panel rounded-4 overflow-hidden h-100">
            <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
              <div>
                <div class="complyon-section-title procurement-section-title text-s">Procurement plan compliance</div>
                <div class="complyon-section-subtitle procurement-section-subtitle text-xs">Committed spend vs the approved annual procurement plan, by category.</div>
              </div>
              <button type="button" class="btn config-navbar-primary-action procurement-action-btn" @click="open_category_modal">
                <i class="bi bi-plus-lg"></i>
                <span>Add category</span>
              </button>
            </div>

            <div class="table-responsive">
              <table class="table procurement-table align-middle mb-0">
                <thead>
                  <tr>
                    <th class="ps-3">Category</th>
                    <th>Planned</th>
                    <th>Committed</th>
                    <th>Utilisation</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in plan_rows"
                    :key="row.category"
                    :class="{ 'bg-light': row.highlighted }"
                    class="procurement-clickable-row"
                    role="button"
                    tabindex="0"
                    @click="open_edit_category_modal(row)"
                    @keydown.enter="open_edit_category_modal(row)"
                    @keydown.space.prevent="open_edit_category_modal(row)"
                  >
                    <td class="ps-3">
                      <div class="procurement-name">{{ row.category }}</div>
                    </td>
                    <td>{{ row.planned }}</td>
                    <td>{{ row.committed }}</td>
                    <td>
                      <div class="d-flex align-items-center gap-2">
                        <div class="procurement-progress flex-grow-1">
                          <div class="progress">
                            <div
                              class="progress-bar"
                              :class="row.utilisation_class === 'success' ? 'bg-success' : row.utilisation_class === 'danger' ? 'bg-danger' : 'bg-warning'"
                              :style="{ width: row.utilisation_pct + '%' }"
                            ></div>
                          </div>
                        </div>
                        <div class="procurement-progress-value">{{ row.utilisation_value }}</div>
                      </div>
                    </td>
                    <td>
                      <span class="badge procurement-badge" :class="row.status.class">{{ row.status.label }}</span>
                    </td>
                    <td class="text-end pe-3">
                      <i class="bi bi-chevron-right procurement-arrow"></i>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </article>
        </div>

        <div class="col-12 col-xl-5">
          <article class="complyon-panel procurement-panel rounded-4 overflow-hidden h-100">
            <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
              <div>
                <div class="complyon-section-title procurement-section-title text-s">Threshold rules</div>
                <div class="complyon-section-subtitle procurement-section-subtitle text-xs">The SCM method required at each value band.</div>
              </div>
              <button type="button" class="btn btn-outline-secondary procurement-action-btn" @click="open_threshold_modal">
                <i class="bi bi-plus-lg"></i>
                <span>Add</span>
              </button>
            </div>

            <div class="px-3 pb-3 pt-2">
              <div
                v-for="row in threshold_rows"
                :key="row.limit"
                class="procurement-request procurement-threshold-item d-flex align-items-center justify-content-between gap-3"
                role="button"
                tabindex="0"
                @click="open_edit_threshold_modal(row)"
                @keydown.enter="open_edit_threshold_modal(row)"
                @keydown.space.prevent="open_edit_threshold_modal(row)"
              >
                <div class="d-flex align-items-center gap-3 min-w-0">
                  <div class="rounded-3 border px-3 py-2 fw-semibold text-nowrap text-s bg-white">{{ row.limit }}</div>
                  <div class="min-w-0">
                    <div class="procurement-request-title text-truncate">{{ row.title }}</div>
                    <div class="procurement-request-meta text-truncate">{{ row.note }}</div>
                  </div>
                </div>
                <i class="bi bi-chevron-right procurement-arrow"></i>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'requisitions'">
      <section class="complyon-panel procurement-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title procurement-section-title text-s">Requisition compliance</div>
            <div class="complyon-section-subtitle procurement-section-subtitle text-xs">
              Every requisition checked against threshold rules and supplier eligibility. Open one for its evidence pack.
            </div>
          </div>
          <button type="button" class="btn config-navbar-primary-action procurement-action-btn" @click="open_requisition_modal">
            <i class="bi bi-plus-lg"></i>
            <span>New requisition</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table procurement-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3">Ref</th>
                <th>Requisition</th>
                <th>Dept</th>
                <th>Value</th>
                <th>Threshold</th>
                <th>Eligibility</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in requisition_rows"
                :key="row.id"
                :class="{ 'bg-light': row.highlighted }"
                class="procurement-clickable-row"
                role="button"
                tabindex="0"
                @click="open_requisition_drawer(row)"
                @keydown.enter="open_requisition_drawer(row)"
                @keydown.space.prevent="open_requisition_drawer(row)"
              >
                <td class="ps-3">
                  <div class="procurement-id">{{ row.id }}</div>
                </td>
                <td>
                  <div class="procurement-name">{{ row.requisition }}</div>
                </td>
                <td>{{ row.dept }}</td>
                <td>{{ row.value }}</td>
                <td>{{ row.threshold }}</td>
                <td>
                  <span class="badge procurement-badge" :class="row.eligibility.class">{{ row.eligibility.label }}</span>
                </td>
                <td>
                  <span class="badge procurement-badge" :class="row.status.class">{{ row.status.label }}</span>
                </td>
                <td class="text-end pe-3">
                  <i class="bi bi-chevron-right procurement-arrow"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'bids'">
      <section class="row g-3">
        <div class="col-12">
          <article class="complyon-panel procurement-panel rounded-4 overflow-hidden">
            <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
              <div>
                <div class="complyon-section-title procurement-section-title text-s">Bid committee declarations, evaluation & adjudication</div>
                <div class="complyon-section-subtitle procurement-section-subtitle text-xs">Bid records with declarations of interest, evaluation scoring and recommendations.</div>
              </div>
              <button type="button" class="btn config-navbar-primary-action procurement-action-btn" @click="open_bid_modal">
                <i class="bi bi-plus-lg"></i>
                <span>New bid record</span>
              </button>
            </div>

            <div class="table-responsive">
              <table class="table procurement-table align-middle mb-0">
                <thead>
                  <tr>
                    <th class="ps-3">Ref</th>
                    <th>Requisition</th>
                    <th>Committee</th>
                    <th>Declarations</th>
                    <th>Recommendation</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in bid_rows"
                    :key="row.id"
                    class="procurement-clickable-row"
                    role="button"
                    tabindex="0"
                    @click="open_edit_bid_modal(row)"
                    @keydown.enter.prevent="open_edit_bid_modal(row)"
                    @keydown.space.prevent="open_edit_bid_modal(row)"
                  >
                    <td class="ps-3">
                      <div class="procurement-id">{{ row.id }}</div>
                    </td>
                    <td>
                      <div class="procurement-name">{{ row.requisition }}</div>
                    </td>
                    <td>{{ row.committee }}</td>
                    <td>
                      <span class="badge procurement-badge" :class="row.declarations.class">{{ row.declarations.label }}</span>
                    </td>
                    <td>{{ row.recommendation }}</td>
                    <td>
                      <span class="badge procurement-badge" :class="row.status.class">{{ row.status.label }}</span>
                    </td>
                    <td class="text-end pe-3">
                      <i class="bi bi-chevron-right procurement-arrow"></i>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </article>
        </div>

        <div class="col-12">
          <article class="complyon-panel procurement-panel rounded-4 overflow-hidden">
            <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
              <div>
                <div class="complyon-section-title procurement-section-title text-s">Award approvals</div>
                <div class="complyon-section-subtitle procurement-section-subtitle text-xs">Contract awards and the authority that approved each.</div>
              </div>
              <button type="button" class="btn config-navbar-primary-action procurement-action-btn" @click="open_award_modal">
                <i class="bi bi-plus-lg"></i>
                <span>New award</span>
              </button>
            </div>

            <div class="table-responsive">
              <table class="table procurement-table align-middle mb-0">
                <thead>
                  <tr>
                    <th class="ps-3">Ref</th>
                    <th>Awarded supplier</th>
                    <th>For</th>
                    <th>Value</th>
                    <th>Approved by</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in award_rows"
                    :key="row.id"
                    class="procurement-clickable-row"
                    role="button"
                    tabindex="0"
                    @click="open_edit_award_modal(row)"
                    @keydown.enter.prevent="open_edit_award_modal(row)"
                    @keydown.space.prevent="open_edit_award_modal(row)"
                  >
                    <td class="ps-3">
                      <div class="procurement-id">{{ row.id }}</div>
                    </td>
                    <td>
                      <div class="procurement-name">{{ row.supplier }}</div>
                    </td>
                    <td>{{ row.for }}</td>
                    <td>{{ row.value }}</td>
                    <td>{{ row.approved_by }}</td>
                    <td>
                      <span class="badge procurement-badge" :class="row.status.class">{{ row.status.label }}</span>
                    </td>
                    <td class="text-end pe-3">
                      <i class="bi bi-chevron-right procurement-arrow"></i>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </article>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'deviations'">
      <section class="row g-3">
        <div class="col-12">
          <article class="complyon-panel procurement-panel rounded-4 overflow-hidden">
            <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
              <div>
                <div class="complyon-section-title procurement-section-title text-s">Deviation register</div>
                <div class="complyon-section-subtitle procurement-section-subtitle text-xs">Deviations from normal SCM process with the justification and approval trail.</div>
              </div>
              <button type="button" class="btn config-navbar-primary-action procurement-action-btn" @click="open_deviation_modal('Deviation')">
                <i class="bi bi-plus-lg"></i>
                <span>Log</span>
              </button>
            </div>

            <div class="table-responsive">
              <table class="table procurement-table align-middle mb-0">
                <thead>
                  <tr>
                    <th class="ps-3">Ref</th>
                    <th>Title</th>
                    <th>Value</th>
                    <th>Justification</th>
                    <th>Approved by</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in deviation_rows"
                    :key="row.id"
                    class="procurement-clickable-row"
                    role="button"
                    tabindex="0"
                    @click="open_edit_deviation_modal(row, 'deviation')"
                    @keydown.enter.prevent="open_edit_deviation_modal(row, 'deviation')"
                    @keydown.space.prevent="open_edit_deviation_modal(row, 'deviation')"
                  >
                    <td class="ps-3">
                      <div class="procurement-id">{{ row.id }}</div>
                    </td>
                    <td>
                      <div class="procurement-name">{{ row.title }}</div>
                    </td>
                    <td>{{ row.value }}</td>
                    <td>{{ row.justification }}</td>
                    <td>{{ row.approved_by }}</td>
                    <td>
                      <span class="badge procurement-badge" :class="row.status.class">{{ row.status.label }}</span>
                    </td>
                    <td class="text-end pe-3">
                      <i class="bi bi-chevron-right procurement-arrow"></i>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </article>
        </div>

        <div class="col-12">
          <article class="complyon-panel procurement-panel rounded-4 overflow-hidden">
            <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
              <div>
                <div class="complyon-section-title procurement-section-title text-s">Emergency procurement register</div>
                <div class="complyon-section-subtitle procurement-section-subtitle text-xs">Emergency / urgent procurements requiring ratification and, where applicable, condonement.</div>
              </div>
              <button type="button" class="btn config-navbar-primary-action procurement-action-btn" @click="open_deviation_modal('Emergency')">
                <i class="bi bi-plus-lg"></i>
                <span>Log</span>
              </button>
            </div>

            <div class="table-responsive">
              <table class="table procurement-table align-middle mb-0">
                <thead>
                  <tr>
                    <th class="ps-3">Ref</th>
                    <th>Title</th>
                    <th>Value</th>
                    <th>Justification</th>
                    <th>Approved by</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in emergency_rows"
                    :key="row.id"
                    class="procurement-clickable-row"
                    role="button"
                    tabindex="0"
                    @click="open_edit_deviation_modal(row, 'emergency')"
                    @keydown.enter.prevent="open_edit_deviation_modal(row, 'emergency')"
                    @keydown.space.prevent="open_edit_deviation_modal(row, 'emergency')"
                  >
                    <td class="ps-3">
                      <div class="procurement-id">{{ row.id }}</div>
                    </td>
                    <td>
                      <div class="procurement-name">{{ row.title }}</div>
                    </td>
                    <td>{{ row.value }}</td>
                    <td>{{ row.justification }}</td>
                    <td>{{ row.approved_by }}</td>
                    <td>
                      <span class="badge procurement-badge" :class="row.status.class">{{ row.status.label }}</span>
                    </td>
                    <td class="text-end pe-3">
                      <i class="bi bi-chevron-right procurement-arrow"></i>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </article>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'irregularities'">
      <section class="row g-3 mb-3">
        <div
          v-for="card in irregularity_cards"
          :key="card.label"
          class="col-12 col-md-6 col-xl-3"
        >
          <article class="complyon-card procurement-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label procurement-section-title text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value procurement-section-title mt-1 text-l" :class="card.accent === 'red' ? 'text-danger' : 'text-warning-emphasis'">
              {{ card.value }}
            </div>
            <div class="complyon-stat-note procurement-section-subtitle mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel procurement-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title procurement-section-title text-s">Irregular expenditure & red-flag detection</div>
            <div class="complyon-section-subtitle procurement-section-subtitle text-xs">Split orders, supplier concentration and threshold breaches — the patterns auditors look for.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action procurement-action-btn" @click="open_irregularity_modal">
            <i class="bi bi-plus-lg"></i>
            <span>Log flag</span>
          </button>
        </div>

        <div class="px-3 pb-2">
          <div
            v-for="item in irregularity_rows"
            :key="item.id"
            class="procurement-request d-flex align-items-center justify-content-between gap-3 procurement-clickable-row"
            :class="{ 'bg-light px-2 rounded-3': item.highlighted }"
            role="button"
            tabindex="0"
            @click="open_edit_irregularity_modal(item)"
            @keydown.enter.prevent="open_edit_irregularity_modal(item)"
            @keydown.space.prevent="open_edit_irregularity_modal(item)"
          >
            <div class="d-flex align-items-start gap-2 min-w-0">
              <span class="procurement-request-icon d-inline-flex align-items-center justify-content-center">
                <i class="bi" :class="item.icon"></i>
              </span>
              <div class="min-w-0">
                <div class="procurement-request-title text-truncate">{{ item.title }}</div>
                <div class="procurement-request-meta">{{ item.meta }}</div>
              </div>
            </div>

            <div class="d-flex flex-column align-items-end gap-1 flex-shrink-0">
              <span class="badge rounded-pill procurement-badge" :class="item.badge_class">{{ item.badge }}</span>
              <div class="procurement-progress-value">{{ item.value }}</div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div
      v-if="procurement_modal_overlay_open"
      class="procurement-modal-backdrop"
      @click="procurement_modal_close_handler()"
    ></div>

    <div v-if="category_modal_open" class="procurement-modal-wrap">
      <div class="procurement-modal">
        <form @submit.prevent="submit_category_modal">
          <div class="procurement-modal-header">
            <div>
              <h2 class="procurement-modal-title mb-1">{{ category_modal_title }}</h2>
              <p class="procurement-modal-subtitle mb-0">A line in the approved annual procurement plan; commitment is tracked against it.</p>
            </div>
            <button type="button" class="procurement-modal-close" @click="close_category_modal" aria-label="Close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="procurement-modal-body">
            <div class="procurement-modal-field">
              <label class="procurement-modal-label" for="procurement-category-name">Category</label>
              <input
                id="procurement-category-name"
                v-model="category_form.category"
                type="text"
                class="form-control procurement-modal-input"
                placeholder="e.g. ICT & infrastructure"
              />
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-category-budget">Planned budget (R)</label>
                <input
                  id="procurement-category-budget"
                  v-model="category_form.planned_budget"
                  type="text"
                  inputmode="numeric"
                  class="form-control procurement-modal-input"
                />
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-category-committed">Committed to date (R)</label>
                <input
                  id="procurement-category-committed"
                  v-model="category_form.committed_to_date"
                  type="text"
                  inputmode="numeric"
                  class="form-control procurement-modal-input"
                />
              </div>
            </div>

            <div class="procurement-modal-field">
              <label class="procurement-modal-label" for="procurement-category-status">Status</label>
              <select
                id="procurement-category-status"
                v-model="category_form.status"
                class="form-select procurement-modal-input"
              >
                <option v-for="status in category_status_options" :key="status" :value="status">{{ status }}</option>
              </select>
            </div>
          </div>

          <div class="procurement-modal-footer">
            <button type="button" class="btn btn-link procurement-modal-cancel" @click="close_category_modal">Cancel</button>
            <button type="submit" class="btn config-navbar-primary-action procurement-modal-submit">
              <i class="bi bi-plus-lg" v-if="category_modal_mode === 'create'"></i>
              <i class="bi bi-check2" v-else></i>
              <span>{{ category_modal_submit_label }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="threshold_modal_open" class="procurement-modal-wrap">
      <div class="procurement-modal">
        <form @submit.prevent="submit_threshold_modal">
          <div class="procurement-modal-header">
            <div>
              <h2 class="procurement-modal-title mb-1">{{ threshold_modal_title }}</h2>
              <p class="procurement-modal-subtitle mb-0">A procurement value band and the SCM method, approver and evidence it requires.</p>
            </div>
            <button type="button" class="procurement-modal-close" @click="close_threshold_modal" aria-label="Close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="procurement-modal-body">
            <div class="procurement-modal-field">
              <label class="procurement-modal-label" for="procurement-rule-band">Value band</label>
              <input
                id="procurement-rule-band"
                v-model="threshold_form.value_band"
                type="text"
                class="form-control procurement-modal-input"
                placeholder="e.g. R 30 001 - R 500 000"
              />
            </div>

            <div class="procurement-modal-field">
              <label class="procurement-modal-label" for="procurement-rule-method">Required method</label>
              <input
                id="procurement-rule-method"
                v-model="threshold_form.required_method"
                type="text"
                class="form-control procurement-modal-input"
                placeholder="e.g. Formal written quotations (min 3)"
              />
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-rule-approver">Approver</label>
                <input
                  id="procurement-rule-approver"
                  v-model="threshold_form.approver"
                  type="text"
                  class="form-control procurement-modal-input"
                  placeholder="e.g. Bid committee (BAC)"
                />
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-rule-evidence">Required evidence</label>
                <input
                  id="procurement-rule-evidence"
                  v-model="threshold_form.required_evidence"
                  type="text"
                  class="form-control procurement-modal-input"
                  placeholder="e.g. RFQ pack + scoring"
                />
              </div>
            </div>
          </div>

          <div class="procurement-modal-footer procurement-modal-footer-split">
            <button
              v-if="threshold_modal_mode === 'edit'"
              type="button"
              class="btn btn-link procurement-modal-delete"
              @click="delete_threshold_rule"
            >
              <i class="bi bi-trash3 me-1"></i>
              <span>Delete</span>
            </button>
            <span v-else></span>

            <div class="d-flex align-items-center gap-2">
              <button type="button" class="btn btn-link procurement-modal-cancel" @click="close_threshold_modal">Cancel</button>
              <button type="submit" class="btn config-navbar-primary-action procurement-modal-submit">
                <i class="bi bi-plus-lg" v-if="threshold_modal_mode === 'create'"></i>
                <i class="bi bi-check2" v-else></i>
                <span>{{ threshold_modal_submit_label }}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div v-if="requisition_modal_open" class="procurement-modal-wrap">
      <div class="procurement-modal">
        <form @submit.prevent="submit_requisition_modal">
          <div class="procurement-modal-header">
            <div>
              <h2 class="procurement-modal-title mb-1">{{ requisition_modal_mode === 'edit' ? 'Edit requisition' : 'New requisition' }}</h2>
              <p class="procurement-modal-subtitle mb-0">A purchase request, checked against threshold rules and supplier eligibility.</p>
            </div>
            <button type="button" class="procurement-modal-close" @click="close_requisition_modal" aria-label="Close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="procurement-modal-body">
            <div class="procurement-modal-field">
              <label class="procurement-modal-label" for="procurement-requisition-name">Requisition</label>
              <input
                id="procurement-requisition-name"
                v-model="requisition_form.requisition"
                type="text"
                class="form-control procurement-modal-input"
                placeholder="e.g. Network upgrade"
              />
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-requisition-department">Department</label>
                <input
                  id="procurement-requisition-department"
                  v-model="requisition_form.department"
                  type="text"
                  class="form-control procurement-modal-input"
                  placeholder="e.g. IT"
                />
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-requisition-value">Value (R)</label>
                <input
                  id="procurement-requisition-value"
                  v-model="requisition_form.value"
                  type="text"
                  inputmode="numeric"
                  class="form-control procurement-modal-input"
                  placeholder="e.g. 180000"
                />
              </div>
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-requisition-method">Procurement method</label>
                <select
                  id="procurement-requisition-method"
                  v-model="requisition_form.procurement_method"
                  class="form-select procurement-modal-input"
                >
                  <option v-for="option in requisition_method_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-requisition-threshold">Threshold band</label>
                <select
                  id="procurement-requisition-threshold"
                  v-model="requisition_form.threshold_band"
                  class="form-select procurement-modal-input"
                >
                  <option v-for="option in requisition_threshold_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-requisition-stage">Stage</label>
                <input
                  id="procurement-requisition-stage"
                  v-model="requisition_form.stage"
                  type="text"
                  class="form-control procurement-modal-input"
                  placeholder="e.g. RFQ evaluation"
                />
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-requisition-supplier">Selected supplier</label>
                <input
                  id="procurement-requisition-supplier"
                  v-model="requisition_form.selected_supplier"
                  type="text"
                  class="form-control procurement-modal-input"
                  placeholder="Supplier name or -"
                />
              </div>
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-requisition-eligibility">Supplier eligibility</label>
                <select
                  id="procurement-requisition-eligibility"
                  v-model="requisition_form.supplier_eligibility"
                  class="form-select procurement-modal-input"
                >
                  <option v-for="option in requisition_eligibility_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-requisition-status">Compliance status</label>
                <select
                  id="procurement-requisition-status"
                  v-model="requisition_form.compliance_status"
                  class="form-select procurement-modal-input"
                >
                  <option v-for="option in requisition_status_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
            </div>

            <div class="procurement-modal-field">
              <label class="procurement-modal-label" for="procurement-requisition-note">Note</label>
              <textarea
                id="procurement-requisition-note"
                v-model="requisition_form.note"
                class="form-control procurement-modal-input procurement-modal-textarea"
                rows="3"
                placeholder="Compliance context"
              ></textarea>
            </div>
          </div>

          <div class="procurement-modal-footer">
            <button type="button" class="btn btn-link procurement-modal-cancel" @click="close_requisition_modal">Cancel</button>
            <button type="submit" class="btn config-navbar-primary-action procurement-modal-submit">
              <i class="bi" :class="requisition_modal_mode === 'edit' ? 'bi-check2' : 'bi-plus-lg'"></i>
              <span>{{ requisition_modal_mode === 'edit' ? 'Save changes' : 'Create' }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="bid_modal_open" class="procurement-modal-wrap">
      <div class="procurement-modal">
        <form @submit.prevent="submit_bid_modal">
          <div class="procurement-modal-header">
            <div>
              <h2 class="procurement-modal-title mb-1">{{ bid_modal_mode === 'edit' ? 'Edit bid record' : 'New bid record' }}</h2>
              <p class="procurement-modal-subtitle mb-0">Bid committee evaluation/adjudication, with declarations of interest.</p>
            </div>
            <button type="button" class="procurement-modal-close" @click="close_bid_modal" aria-label="Close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="procurement-modal-body">
            <div class="procurement-modal-field">
              <label class="procurement-modal-label" for="procurement-bid-requisition">Requisition</label>
              <input
                id="procurement-bid-requisition"
                v-model="bid_form.requisition"
                type="text"
                class="form-control procurement-modal-input"
                placeholder="e.g. Office renovation (REQ-211)"
              />
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-bid-committee">Committee</label>
                <select
                  id="procurement-bid-committee"
                  v-model="bid_form.committee"
                  class="form-select procurement-modal-input"
                >
                  <option v-for="option in bid_committee_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-bid-chairperson">Chairperson</label>
                <input
                  id="procurement-bid-chairperson"
                  v-model="bid_form.chairperson"
                  type="text"
                  class="form-control procurement-modal-input"
                  placeholder="Name (role)"
                />
              </div>
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-bid-declarations">Declarations of interest</label>
                <input
                  id="procurement-bid-declarations"
                  v-model="bid_form.declarations"
                  type="text"
                  class="form-control procurement-modal-input"
                  placeholder="e.g. Complete / 1 conflict declared"
                />
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-bid-evaluation">Evaluation</label>
                <input
                  id="procurement-bid-evaluation"
                  v-model="bid_form.evaluation"
                  type="text"
                  class="form-control procurement-modal-input"
                  placeholder="e.g. Scored 4 bidders"
                />
              </div>
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-bid-recommendation">Recommendation</label>
                <input
                  id="procurement-bid-recommendation"
                  v-model="bid_form.recommendation"
                  type="text"
                  class="form-control procurement-modal-input"
                  placeholder="Recommended bidder"
                />
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-bid-status">Status</label>
                <select
                  id="procurement-bid-status"
                  v-model="bid_form.status"
                  class="form-select procurement-modal-input"
                >
                  <option v-for="option in bid_status_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
            </div>
          </div>

          <div class="procurement-modal-footer procurement-modal-footer-split">
            <button
              v-if="bid_modal_mode === 'edit'"
              type="button"
              class="btn btn-link procurement-modal-delete"
              @click="delete_bid_modal"
            >
              <i class="bi bi-trash3 me-1"></i>
              <span>Delete</span>
            </button>
            <span v-else></span>

            <div class="d-flex align-items-center gap-2">
              <button type="button" class="btn btn-link procurement-modal-cancel" @click="close_bid_modal">Cancel</button>
              <button type="submit" class="btn config-navbar-primary-action procurement-modal-submit">
                <i class="bi" :class="bid_modal_mode === 'edit' ? 'bi-check2' : 'bi-plus-lg'"></i>
                <span>{{ bid_modal_mode === 'edit' ? 'Save changes' : 'Create' }}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div v-if="award_modal_open" class="procurement-modal-wrap">
      <div class="procurement-modal">
        <form @submit.prevent="submit_award_modal">
          <div class="procurement-modal-header">
            <div>
              <h2 class="procurement-modal-title mb-1">{{ award_modal_mode === 'edit' ? 'Edit award' : 'New award' }}</h2>
              <p class="procurement-modal-subtitle mb-0">A contract award and its approval authority.</p>
            </div>
            <button type="button" class="procurement-modal-close" @click="close_award_modal" aria-label="Close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="procurement-modal-body">
            <div class="procurement-modal-field">
              <label class="procurement-modal-label" for="procurement-award-supplier">Awarded supplier</label>
              <input
                id="procurement-award-supplier"
                v-model="award_form.supplier"
                type="text"
                class="form-control procurement-modal-input"
                placeholder="Supplier name"
              />
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-award-requisition">Requisition</label>
                <input
                  id="procurement-award-requisition"
                  v-model="award_form.requisition"
                  type="text"
                  class="form-control procurement-modal-input"
                  placeholder="What was procured"
                />
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-award-value">Value (R)</label>
                <input
                  id="procurement-award-value"
                  v-model="award_form.value"
                  type="text"
                  inputmode="numeric"
                  class="form-control procurement-modal-input"
                />
              </div>
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-award-method">Method</label>
                <select
                  id="procurement-award-method"
                  v-model="award_form.method"
                  class="form-select procurement-modal-input"
                >
                  <option v-for="option in award_method_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-award-approved-by">Approved by</label>
                <input
                  id="procurement-award-approved-by"
                  v-model="award_form.approved_by"
                  type="text"
                  class="form-control procurement-modal-input"
                  placeholder="e.g. Accounting Officer"
                />
              </div>
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-award-date">Date</label>
                <input
                  id="procurement-award-date"
                  v-model="award_form.date"
                  type="text"
                  class="form-control procurement-modal-input"
                  placeholder="e.g. 10 May 2026"
                />
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-award-status">Status</label>
                <select
                  id="procurement-award-status"
                  v-model="award_form.status"
                  class="form-select procurement-modal-input"
                >
                  <option v-for="option in award_status_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
            </div>
          </div>

          <div class="procurement-modal-footer procurement-modal-footer-split">
            <button
              v-if="award_modal_mode === 'edit'"
              type="button"
              class="btn btn-link procurement-modal-delete"
              @click="delete_award_modal"
            >
              <i class="bi bi-trash3 me-1"></i>
              <span>Delete</span>
            </button>
            <span v-else></span>

            <div class="d-flex align-items-center gap-2">
              <button type="button" class="btn btn-link procurement-modal-cancel" @click="close_award_modal">Cancel</button>
              <button type="submit" class="btn config-navbar-primary-action procurement-modal-submit">
                <i class="bi" :class="award_modal_mode === 'edit' ? 'bi-check2' : 'bi-plus-lg'"></i>
                <span>{{ award_modal_mode === 'edit' ? 'Save changes' : 'Create' }}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div v-if="deviation_modal_open" class="procurement-modal-wrap">
      <div class="procurement-modal">
        <form @submit.prevent="submit_deviation_modal">
          <div class="procurement-modal-header">
            <div>
              <h2 class="procurement-modal-title mb-1">{{ deviation_modal_mode === 'edit' ? 'Edit deviation / emergency' : 'New deviation / emergency' }}</h2>
              <p class="procurement-modal-subtitle mb-0">A deviation from normal SCM process or an emergency procurement — must be justified and approved.</p>
            </div>
            <button type="button" class="procurement-modal-close" @click="close_deviation_modal" aria-label="Close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="procurement-modal-body">
            <div class="procurement-modal-field">
              <label class="procurement-modal-label" for="procurement-deviation-title">Title</label>
              <input
                id="procurement-deviation-title"
                v-model="deviation_form.title"
                type="text"
                class="form-control procurement-modal-input"
                placeholder="e.g. Sole-source licence renewal"
              />
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-deviation-type">Type</label>
                <select
                  id="procurement-deviation-type"
                  v-model="deviation_form.type"
                  class="form-select procurement-modal-input"
                >
                  <option v-for="option in deviation_type_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-deviation-value">Value (R)</label>
                <input
                  id="procurement-deviation-value"
                  v-model="deviation_form.value"
                  type="text"
                  inputmode="numeric"
                  class="form-control procurement-modal-input"
                />
              </div>
            </div>

            <div class="procurement-modal-field">
              <label class="procurement-modal-label" for="procurement-deviation-justification">Justification</label>
              <input
                id="procurement-deviation-justification"
                v-model="deviation_form.justification"
                type="text"
                class="form-control procurement-modal-input"
                placeholder="Why normal process was deviated from"
              />
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-deviation-approved-by">Approved by</label>
                <input
                  id="procurement-deviation-approved-by"
                  v-model="deviation_form.approved_by"
                  type="text"
                  class="form-control procurement-modal-input"
                  placeholder="e.g. Accounting Officer"
                />
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-deviation-date">Date</label>
                <input
                  id="procurement-deviation-date"
                  v-model="deviation_form.date"
                  type="text"
                  class="form-control procurement-modal-input"
                  placeholder="e.g. 02 Apr 2026"
                />
              </div>
            </div>

            <div class="procurement-modal-field">
              <label class="procurement-modal-label" for="procurement-deviation-status">Status</label>
              <select
                id="procurement-deviation-status"
                v-model="deviation_form.status"
                class="form-select procurement-modal-input"
              >
                <option v-for="option in deviation_status_options" :key="option" :value="option">{{ option }}</option>
              </select>
            </div>
          </div>

          <div class="procurement-modal-footer procurement-modal-footer-split">
            <button
              v-if="deviation_modal_mode === 'edit'"
              type="button"
              class="btn btn-link procurement-modal-delete"
              @click="delete_deviation_modal"
            >
              <i class="bi bi-trash3 me-1"></i>
              <span>Delete</span>
            </button>
            <span v-else></span>

            <div class="d-flex align-items-center gap-2">
              <button type="button" class="btn btn-link procurement-modal-cancel" @click="close_deviation_modal">Cancel</button>
              <button type="submit" class="btn config-navbar-primary-action procurement-modal-submit">
                <i class="bi" :class="deviation_modal_mode === 'edit' ? 'bi-check2' : 'bi-plus-lg'"></i>
                <span>{{ deviation_modal_mode === 'edit' ? 'Save changes' : 'Create' }}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div v-if="irregularity_modal_open" class="procurement-modal-wrap">
      <div class="procurement-modal">
        <form @submit.prevent="submit_irregularity_modal">
          <div class="procurement-modal-header">
            <div>
              <h2 class="procurement-modal-title mb-1">{{ irregularity_modal_mode === 'edit' ? 'Edit irregularity flag' : 'New irregularity flag' }}</h2>
              <p class="procurement-modal-subtitle mb-0">A detected SCM irregularity for investigation.</p>
            </div>
            <button type="button" class="procurement-modal-close" @click="close_irregularity_modal" aria-label="Close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>

          <div class="procurement-modal-body">
            <div class="procurement-modal-field">
              <label class="procurement-modal-label" for="procurement-irregularity-flag">Flag</label>
              <input
                id="procurement-irregularity-flag"
                v-model="irregularity_form.flag"
                type="text"
                class="form-control procurement-modal-input"
                placeholder="e.g. Possible split purchase"
              />
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-irregularity-type">Type</label>
                <select
                  id="procurement-irregularity-type"
                  v-model="irregularity_form.type"
                  class="form-select procurement-modal-input"
                >
                  <option v-for="option in irregularity_type_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-irregularity-value">Value exposed (R)</label>
                <input
                  id="procurement-irregularity-value"
                  v-model="irregularity_form.value"
                  type="text"
                  inputmode="numeric"
                  class="form-control procurement-modal-input"
                />
              </div>
            </div>

            <div class="procurement-modal-field">
              <label class="procurement-modal-label" for="procurement-irregularity-detail">Detail</label>
              <textarea
                id="procurement-irregularity-detail"
                v-model="irregularity_form.detail"
                class="form-control procurement-modal-input procurement-modal-textarea"
                rows="3"
                placeholder="What was detected"
              ></textarea>
            </div>

            <div class="procurement-modal-grid">
              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-irregularity-severity">Severity</label>
                <select
                  id="procurement-irregularity-severity"
                  v-model="irregularity_form.severity"
                  class="form-select procurement-modal-input"
                >
                  <option v-for="option in irregularity_severity_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>

              <div class="procurement-modal-field">
                <label class="procurement-modal-label" for="procurement-irregularity-status">Status</label>
                <select
                  id="procurement-irregularity-status"
                  v-model="irregularity_form.status"
                  class="form-select procurement-modal-input"
                >
                  <option v-for="option in irregularity_status_options" :key="option" :value="option">{{ option }}</option>
                </select>
              </div>
            </div>
          </div>

          <div class="procurement-modal-footer procurement-modal-footer-split">
            <button
              v-if="irregularity_modal_mode === 'edit'"
              type="button"
              class="btn btn-link procurement-modal-delete"
              @click="delete_irregularity_modal"
            >
              <i class="bi bi-trash3 me-1"></i>
              <span>Delete</span>
            </button>
            <span v-else></span>

            <div class="d-flex align-items-center gap-2">
              <button type="button" class="btn btn-link procurement-modal-cancel" @click="close_irregularity_modal">Cancel</button>
              <button type="submit" class="btn config-navbar-primary-action procurement-modal-submit">
                <i class="bi" :class="irregularity_modal_mode === 'edit' ? 'bi-check2' : 'bi-plus-lg'"></i>
                <span>{{ irregularity_modal_mode === 'edit' ? 'Save changes' : 'Create' }}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <aside
      v-if="active_tab === 'requisitions' && active_requisition_drawer"
      class="command-centre-drawer supplier-register-drawer procurement-requisition-drawer"
      aria-label="Requisition drawer"
    >
      <div class="command-centre-drawer-header border-bottom">
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div class="min-w-0">
            <div class="command-centre-drawer-kicker text-uppercase">{{ active_requisition_drawer.kicker }}</div>
            <h2 class="command-centre-drawer-title mb-1">{{ active_requisition_drawer.title }}</h2>
            <div class="command-centre-drawer-subtitle">{{ active_requisition_drawer.subtitle }}</div>
          </div>

          <div class="d-flex align-items-center gap-2 flex-shrink-0">
            <span class="badge complyon-badge supplier-register-badge text-xs" :class="active_requisition_drawer.status.class">
              {{ active_requisition_drawer.status.label }}
            </span>
            <button type="button" class="btn btn-link text-decoration-none text-secondary p-0" @click="close_requisition_drawer" aria-label="Close requisition drawer">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="command-centre-drawer-body">
        <div class="supplier-register-drawer-grid">
          <article
            v-for="card in active_requisition_drawer.summary"
            :key="card.label"
            class="supplier-register-drawer-stat"
          >
            <div class="supplier-register-drawer-stat-label">{{ card.label }}</div>
            <div class="procurement-requisition-drawer-value">{{ card.value }}</div>
            <span
              v-if="card.badge"
              class="badge complyon-badge supplier-register-badge text-xs mt-2"
              :class="card.badge.class"
            >
              {{ card.badge.label }}
            </span>
          </article>
        </div>

        <section>
          <div class="supplier-register-drawer-section-head mb-2">SCM compliance checklist</div>
          <div class="supplier-register-drawer-records">
            <article
              v-for="item in active_requisition_drawer.checklist"
              :key="item.label"
              class="supplier-register-drawer-record d-flex align-items-start justify-content-between gap-3"
            >
              <div class="d-flex align-items-start gap-2 min-w-0">
                <span class="supplier-register-drawer-record-icon procurement-requisition-drawer-icon" :class="item.class">
                  <i class="bi" :class="item.class === 'is-pass' ? 'bi-check-circle' : 'bi-x-circle'"></i>
                </span>
                <div class="min-w-0">
                  <div class="supplier-register-drawer-record-title">{{ item.label }}</div>
                </div>
              </div>
              <span class="badge complyon-badge supplier-register-badge text-xs" :class="item.class === 'is-pass' ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis'">
                {{ item.status }}
              </span>
            </article>
          </div>
        </section>

        <section>
          <div class="supplier-register-drawer-section-head mb-2">Evidence pack</div>
          <div class="supplier-register-drawer-records">
            <article
              v-for="item in active_requisition_drawer.evidence_pack"
              :key="item.title"
              class="supplier-register-drawer-record d-flex align-items-start gap-2"
            >
              <span class="supplier-register-drawer-record-icon procurement-requisition-drawer-icon" :class="item.class">
                <i class="bi" :class="item.class === 'is-pass' ? 'bi-file-earmark-check' : 'bi-exclamation-circle'"></i>
              </span>
              <div class="min-w-0 flex-grow-1">
                <div class="supplier-register-drawer-record-title">{{ item.title }}</div>
                <div class="supplier-register-drawer-record-subtitle">{{ item.subtitle }}</div>
              </div>
            </article>
          </div>
        </section>

        <section>
          <div class="supplier-register-drawer-section-head mb-2">Compliance note</div>
          <div class="supplier-register-empty-note">{{ active_requisition_drawer.compliance_note }}</div>
        </section>

        <button type="button" class="btn btn-link px-0 text-decoration-none supplier-register-inline-link procurement-requisition-drawer-link">
          <i class="bi bi-box-arrow-up-right me-2"></i>
          <span>{{ active_requisition_drawer.link_label }}</span>
        </button>
      </div>

      <div class="command-centre-drawer-footer border-top">
        <div class="command-centre-drawer-footer-left">
          <button type="button" class="btn btn-outline-secondary btn-sm procurement-requisition-drawer-footer-btn" @click="export_requisition_evidence">
            <i class="bi bi-download me-1"></i>
            <span>Export evidence pack</span>
          </button>
        </div>
        <div class="command-centre-drawer-footer-actions">
          <button type="button" class="btn config-navbar-primary-action btn-sm procurement-requisition-drawer-footer-btn" @click="open_edit_requisition_modal(requisition_rows.find((row) => row.id === active_requisition_drawer.row_id))">
            <i class="bi bi-pencil-square me-1"></i>
            <span>Edit requisition</span>
          </button>
        </div>
      </div>
    </aside>
  </div>
</div>
